import { trackPageAction } from "lib/analytics/core/actions";
import { useEffect, useState } from "react";
import { TYPO } from "styles/Typo";

const ToggleComponent = ({
  title,
  desc1,
  desc2,
  icon,
  faqAutoExpanded,
  setFaqAutoExpanded,
}: {
  title: string;
  desc1: string;
  desc2: string;
  icon: React.ReactNode;
  faqAutoExpanded?: boolean;
  setFaqAutoExpanded?: (faqAutoExpanded: boolean) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (faqAutoExpanded) {
      setIsOpen(true);
    }
    return () => {
      setIsOpen(false);
    };
  }, [faqAutoExpanded]);
  return (
    <div
      className="overflow-hidden cursor-pointer "
      onClick={() => {
        trackPageAction(isOpen ? "collapse_strat_faq" : "expand_strat_faq");
        setIsOpen(!isOpen);
        setFaqAutoExpanded && setFaqAutoExpanded(false);
      }}
      data-gtm-id="stInfoFaq"
    >
      <div className="flex items-center gap-2 py-4 px-6 strategy-faq-toggle  ">
        <div className="flex items-center justify-center">{icon}</div>
        <div className="flex items-center justify-between grow">
          <p className={`${TYPO.TYPO5_1_MD} text-gray80 grow`}>{title}</p>
          <div
            style={{
              transform: isOpen ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.3s ease",
            }}
          >
            <ArrowIcon />
          </div>
        </div>
      </div>
      <div
        className="flex items-center bg-blue5 overflow-hidden"
        style={{
          maxHeight: isOpen ? "180px" : "0px",
          transition: "max-height 0.3s ease",
        }}
      >
        <ul className="px-6 py-4 flex flex-col gap-2 list-inside ml-6 ">
          <li
            className={`${TYPO.TYPO7_1_RG} text-gray70 list-disc indent-[-17px] custom-word-break`}
          >
            {desc1}
          </li>
          <li
            className={`${TYPO.TYPO7_1_RG} text-gray70 list-disc indent-[-17px] custom-word-break`}
          >
            {desc2}
          </li>
        </ul>
      </div>
    </div>
  );
};

function StrategyFAQ({
  faqAutoExpanded,
  setFaqAutoExpanded,
}: {
  faqAutoExpanded: boolean;
  setFaqAutoExpanded: (faqAutoExpanded: boolean) => void;
}) {
  const INFO = [
    {
      title: "전략이란?",
      desc1:
        "전략은 일정 기간마다 ‘매수하면 좋을 종목’을 선정해서 알려드리는 서비스에요. ",
      desc2:
        "전략의 구성 종목은 리스크웨더의 자체 지표인 ‘투자매력도’를 중심으로 선정돼요.",
      icon: <FAQIcon index={0} />,
    },
    {
      title: "투자매력도가 뭔가요?",
      desc1:
        "투자매력도는 산업 흐름, 주가 탄력성, 재무 상태를 분석해 종합 점수로 표현한 지표예요. ",
      desc2:
        "점수가 높을수록 성장 가능성과 안정성이 높은 종목으로 판단할 수 있어요. ",
      icon: <FAQIcon index={1} />,
    },
    {
      title: "손실 위험이 있나요?",
      desc1:
        "네, 손실위험은 항상 존재해요. 하지만 성과를 전체기간으로 놓고 보면 현재까지 큰 수익을 내고 있는 것을 확인할 수 있을 거예요. ",
      desc2: "전략을 모니터링하며 장기적 투자 관점으로 접근하는 것을 추천해요.",
      icon: <FAQIcon index={2} />,
    },
  ];

  return (
    <div className="py-4 bg-white faq">
      {INFO.map((item, index) => (
        <ToggleComponent
          key={index}
          title={item.title}
          desc1={item.desc1}
          desc2={item.desc2}
          icon={item.icon}
          faqAutoExpanded={index === 1 ? faqAutoExpanded : false}
          setFaqAutoExpanded={index === 1 ? setFaqAutoExpanded : undefined}
        />
      ))}
    </div>
  );
}

const QuestionIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM10.5325 14.3034C10.5489 12.2287 11.137 11.5916 12.1661 10.9545C12.8686 10.5134 13.3913 9.95802 13.3913 9.14122C13.3913 8.22641 12.6889 7.63832 11.7904 7.63832C10.9736 7.63832 10.1895 8.16107 10.1405 9.25558H8.09848C8.14749 7.06657 9.79741 5.93939 11.8067 5.93939C14.0121 5.93939 15.5313 7.16458 15.5313 9.10855C15.5313 10.4318 14.8615 11.2976 13.7997 11.9347C12.8522 12.5064 12.4602 13.0782 12.4438 14.3034V14.4504H10.5325V14.3034ZM11.5454 18.0606C10.8429 18.0606 10.2712 17.4889 10.2712 16.7864C10.2712 16.1003 10.8429 15.5285 11.5454 15.5285C12.2151 15.5285 12.8032 16.1003 12.8032 16.7864C12.8032 17.4889 12.2151 18.0606 11.5454 18.0606Z"
        fill="var(--blue30)"
      />
    </svg>
  );
};

const ArrowIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        d="M8.0302 5.81667L3.1302 10.7167C2.96353 10.8833 2.76909 10.9639 2.54687 10.9583C2.32464 10.9528 2.1302 10.8667 1.96353 10.7C1.79687 10.5333 1.71353 10.3389 1.71353 10.1167C1.71353 9.89444 1.79687 9.7 1.96353 9.53333L7.0802 4.4C7.21353 4.26667 7.36353 4.16667 7.5302 4.1C7.69687 4.03333 7.86353 4 8.0302 4C8.19687 4 8.36353 4.03333 8.5302 4.1C8.69687 4.16667 8.84687 4.26667 8.9802 4.4L14.1135 9.53333C14.2802 9.7 14.3608 9.89722 14.3552 10.125C14.3496 10.3528 14.2635 10.55 14.0969 10.7167C13.9302 10.8833 13.7358 10.9667 13.5135 10.9667C13.2913 10.9667 13.0969 10.8833 12.9302 10.7167L8.0302 5.81667Z"
        fill="var(--gray40)"
      />
    </svg>
  );
};

const FAQIcon = ({ index }: { index: number }) => {
  switch (index) {
    case 0:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M10.6 13.8L8.45 11.65C8.26667 11.4667 8.03333 11.375 7.75 11.375C7.46667 11.375 7.23333 11.4667 7.05 11.65C6.86667 11.8333 6.775 12.0667 6.775 12.35C6.775 12.6333 6.86667 12.8667 7.05 13.05L9.9 15.9C10.1 16.1 10.3333 16.2 10.6 16.2C10.8667 16.2 11.1 16.1 11.3 15.9L16.95 10.25C17.1333 10.0667 17.225 9.83333 17.225 9.55C17.225 9.26667 17.1333 9.03333 16.95 8.85C16.7667 8.66667 16.5333 8.575 16.25 8.575C15.9667 8.575 15.7333 8.66667 15.55 8.85L10.6 13.8ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22Z"
            fill="#34BB7A"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.44844 11.6531L10.5984 13.8031L15.5484 8.85312C15.7318 8.66979 15.9651 8.57812 16.2484 8.57812C16.5318 8.57812 16.7651 8.66979 16.9484 8.85312C17.1318 9.03646 17.2234 9.26979 17.2234 9.55313C17.2234 9.83646 17.1318 10.0698 16.9484 10.2531L11.2984 15.9031C11.0984 16.1031 10.8651 16.2031 10.5984 16.2031C10.3318 16.2031 10.0984 16.1031 9.89844 15.9031L7.04844 13.0531C6.8651 12.8698 6.77344 12.6365 6.77344 12.3531C6.77344 12.0698 6.8651 11.8365 7.04844 11.6531C7.23177 11.4698 7.4651 11.3781 7.74844 11.3781C8.03177 11.3781 8.2651 11.4698 8.44844 11.6531Z"
            fill="white"
          />
        </svg>
      );
    case 1:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <rect width="24" height="24" fill="white" />
          <path
            d="M8.69531 11.875C8.94568 11.875 9.15758 12.0604 9.19043 12.3086L9.55957 15.1152C9.63441 15.6838 10.0473 16.1506 10.6025 16.2939L12.7803 16.8555C12.9602 16.9019 13.0859 17.0642 13.0859 17.25C13.0859 17.4358 12.9602 17.5981 12.7803 17.6445L10.6025 18.2061C10.0473 18.3494 9.63441 18.8162 9.55957 19.3848L9.19043 22.1914C9.15758 22.4396 8.94568 22.625 8.69531 22.625C8.44495 22.625 8.23304 22.4396 8.2002 22.1914L7.83105 19.3848C7.75621 18.8162 7.34336 18.3494 6.78809 18.2061L4.61035 17.6445C4.43047 17.5981 4.30469 17.4358 4.30469 17.25C4.30469 17.0642 4.43047 16.9019 4.61035 16.8555L6.78809 16.2939C7.34336 16.1506 7.75621 15.6838 7.83105 15.1152L8.2002 12.3086C8.23304 12.0604 8.44495 11.875 8.69531 11.875Z"
            fill="#FFCD00"
          />
          <path
            d="M16.5371 2.75C16.9199 2.75 17.2438 3.03363 17.2939 3.41309L17.8008 7.26172C17.8992 8.00971 18.4415 8.62385 19.1719 8.8125L22.1592 9.58301C22.4406 9.65562 22.6377 9.90958 22.6377 10.2002C22.6376 10.4907 22.4405 10.7448 22.1592 10.8174L19.1719 11.5879C18.4417 11.7765 17.8993 12.3899 17.8008 13.1377L17.2939 16.9863C17.244 17.366 16.9201 17.6504 16.5371 17.6504C16.1543 17.6502 15.8312 17.3659 15.7812 16.9863L15.2744 13.1377C15.1759 12.3897 14.6328 11.7764 13.9023 11.5879L10.915 10.8174C10.6339 10.7447 10.4376 10.4906 10.4375 10.2002C10.4375 9.90974 10.6339 9.65578 10.915 9.58301L13.9023 8.8125C14.6328 8.62393 15.176 8.0098 15.2744 7.26172L15.7812 3.41309C15.8314 3.03377 16.1545 2.75019 16.5371 2.75Z"
            fill="#FFCD00"
          />
          <path
            d="M5.2168 3.17578C5.42974 3.17578 5.60991 3.3338 5.6377 3.54492L5.93359 5.79004C5.99226 6.23466 6.3148 6.59885 6.74902 6.71094L8.49121 7.16113C8.64616 7.20112 8.75488 7.34095 8.75488 7.50098C8.75479 7.66092 8.6461 7.80085 8.49121 7.84082L6.74902 8.29004C6.31467 8.40216 5.99213 8.76712 5.93359 9.21191L5.6377 11.457C5.60982 11.668 5.42966 11.8262 5.2168 11.8262C5.00411 11.826 4.82475 11.6679 4.79688 11.457L4.50098 9.21191C4.44245 8.76713 4.11988 8.40217 3.68555 8.29004L1.94336 7.84082C1.78847 7.80085 1.67978 7.66092 1.67969 7.50098C1.67969 7.34095 1.78841 7.20112 1.94336 7.16113L3.68555 6.71094C4.11969 6.59879 4.44233 6.23461 4.50098 5.79004L4.79688 3.54492C4.82464 3.33393 5.00403 3.17597 5.2168 3.17578Z"
            fill="#FFCD00"
          />
        </svg>
      );
    case 2:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <rect width="24" height="24" fill="white" />
          <path
            d="M8.62986 9.88823H12.0699L14.7521 7.3549L17.4343 9.88823H18.341C20.2065 9.88823 19.3965 10.7327 21.7188 12.2105C21.7188 14.215 21.7188 18.7549 21.7188 18.7549C21.7188 19.9208 20.7736 20.866 19.6076 20.866H4.82986C3.66393 20.866 2.71875 19.9208 2.71875 18.7549L2.71875 4.18823L8.62986 9.88823Z"
            fill="#93C8FF"
          />
          <path
            d="M2.97853 3.49878C3.33536 3.07951 3.93757 3.0146 4.36829 3.33203L4.45189 3.40067L10.2452 8.65803C10.4837 8.87443 10.8466 8.87744 11.0886 8.66503L14.0138 6.09778C14.4607 5.70556 15.1062 5.689 15.5696 6.04304L15.6597 6.11841L21.37 11.3834C21.8001 11.7799 21.8375 12.4615 21.4536 12.9056C21.0936 13.322 20.4909 13.3819 20.0626 13.061L19.9795 12.9919L15.2304 8.61311C14.9922 8.39349 14.6267 8.38904 14.3833 8.60278L11.4663 11.1635C10.9926 11.5793 10.2953 11.5734 9.82824 11.1497L3.07354 5.0202L2.99688 4.94314C2.63684 4.54283 2.62175 3.91801 2.97853 3.49878Z"
            fill="#027FF3"
          />
        </svg>
      );
    default:
      return <QuestionIcon />;
  }
};

export default StrategyFAQ;
